import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { ArrowLeft, Bell, BellOff, Clock, Sun, Moon, Coffee, BookOpen, Dumbbell, Heart, Lightbulb, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { subscribeToPush } from '@/utils/pushNotifications';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const REMINDER_TYPES = [
  {
    id: 'daily',
    title: 'Daglig påminnelse',
    description: 'En vänlig knuff varje dag så att Livon inte glöms bort',
    icon: Bell,
    defaultTime: '19:00',
    message: 'Hur har din dag varit? Ta en stund för dig själv i Livon.'
  },
  {
    id: 'morning',
    title: 'Morgonpåminnelse',
    description: 'Starta dagen med en positiv tanke',
    icon: Sun,
    defaultTime: '08:00',
    message: 'God morgon! Hur vill du att din dag ska bli?'
  },
  {
    id: 'journal',
    title: 'Dagbok',
    description: 'Påminnelse att skriva i din dagbok',
    icon: BookOpen,
    defaultTime: '20:00',
    message: 'Dags att reflektera över dagen. Hur mår du?'
  },
  {
    id: 'gratitude',
    title: 'Tacksamhet',
    description: 'Daglig tacksamhetsövning',
    icon: Heart,
    defaultTime: '21:00',
    message: 'Vad är du tacksam för idag?'
  },
  {
    id: 'exercise',
    title: 'Träning',
    description: 'Påminnelse om rörelse',
    icon: Dumbbell,
    defaultTime: '17:00',
    message: 'Dags att röra på dig! Din kropp kommer tacka dig.'
  },
  {
    id: 'evening',
    title: 'Kvällsrutin',
    description: 'Förbered för en god natts sömn',
    icon: Moon,
    defaultTime: '22:00',
    message: 'Dags att varva ner. Hur har din dag varit?'
  },
  {
    id: 'break',
    title: 'Pauspåminnelse',
    description: 'Ta regelbundna pauser under dagen',
    icon: Coffee,
    defaultTime: '14:00',
    message: 'Dags för en kort paus. Andas och sträck på dig!'
  }
];

export default function RemindersPage() {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [reminders, setReminders] = useState({});
  const [loading, setLoading] = useState(true);
  const [notificationPermission, setNotificationPermission] = useState('default');

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/reminders/settings`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setReminders(res.data.reminders);
      } catch (e) {
        toast.error('Kunde inte ladda påminnelser');
      } finally {
        setLoading(false);
      }
    };
    load();

    if ('Notification' in window) {
      setNotificationPermission(Notification.permission);
    }
  }, [token]);

  const saveToServer = useCallback(async (updated) => {
    try {
      await axios.put(`${API}/reminders/settings`, { reminders: updated }, {
        headers: { Authorization: `Bearer ${token}` }
      });
    } catch (e) {
      toast.error('Kunde inte spara påminnelsen');
    }
  }, [token]);

  const ensurePushEnabled = async () => {
    if (!('Notification' in window) || !('PushManager' in window)) {
      toast.error('Din enhet stödjer inte notiser här. På iPhone: installera Livon på hemskärmen först.');
      return false;
    }
    const result = await subscribeToPush(token);
    setNotificationPermission(Notification.permission);
    if (!result.success) {
      if (result.reason === 'denied') {
        toast.error('Du behöver tillåta notiser i webbläsarens inställningar');
      } else {
        toast.error('Kunde inte aktivera notiser');
      }
      return false;
    }
    return true;
  };

  const toggleReminder = async (reminderId) => {
    const type = REMINDER_TYPES.find(r => r.id === reminderId);
    const current = reminders[reminderId] || { enabled: false, time: type?.defaultTime };

    if (!current.enabled) {
      const ok = await ensurePushEnabled();
      if (!ok) return;
    }

    const updated = {
      ...reminders,
      [reminderId]: { ...current, enabled: !current.enabled }
    };
    setReminders(updated);
    await saveToServer(updated);

    if (!current.enabled) {
      toast.success(`Påminnelse aktiverad kl ${current.time}`);
    }
  };

  const updateTime = async (reminderId, time) => {
    const updated = {
      ...reminders,
      [reminderId]: { ...reminders[reminderId], time }
    };
    setReminders(updated);
    await saveToServer(updated);
  };

  const requestPermission = async () => {
    const ok = await ensurePushEnabled();
    if (ok) toast.success('Notiser aktiverade!');
  };

  const testNotification = (reminderId) => {
    if (notificationPermission !== 'granted') {
      requestPermission();
      return;
    }
    const reminderType = REMINDER_TYPES.find(r => r.id === reminderId);
    if (reminderType) {
      new Notification('Livon', {
        body: reminderType.message,
        icon: '/icons/icon-192.png',
        tag: `test-${reminderId}`
      });
      toast.success('Testnotis skickad');
    }
  };

  const enabledCount = Object.values(reminders).filter(r => r?.enabled).length;

  return (
    <div className="min-h-screen bg-background" data-testid="reminders-page">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/category/planning')}
            className="rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Tillbaka
          </Button>
          <h1 className="font-heading text-xl">Påminnelser</h1>
          <div className="w-20" />
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-8 space-y-8">
        {/* Intro */}
        <div className="text-center space-y-3 animate-fade-in">
          <Bell className="w-12 h-12 mx-auto text-primary" />
          <h2 className="font-heading text-3xl font-light">Dina påminnelser</h2>
          <p className="text-muted-foreground max-w-lg mx-auto">
            Få notiser som hjälper dig att ta hand om dig själv — även när appen är stängd.
          </p>
        </div>

        {/* Status */}
        <div className="bg-card rounded-2xl p-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            {notificationPermission === 'granted' ? (
              <Bell className="w-8 h-8 text-primary" />
            ) : (
              <BellOff className="w-8 h-8 text-muted-foreground" />
            )}
            <div>
              <p className="font-medium">
                {notificationPermission === 'granted' 
                  ? `${enabledCount} påminnelser aktiva` 
                  : 'Notiser ej aktiverade'}
              </p>
              <p className="text-sm text-muted-foreground">
                {notificationPermission === 'granted'
                  ? 'Du får notiser på valda tider'
                  : 'Aktivera för att få påminnelser'}
              </p>
            </div>
          </div>
          {notificationPermission !== 'granted' && (
            <Button onClick={requestPermission} className="rounded-full" data-testid="enable-notifications-btn">
              Aktivera
            </Button>
          )}
        </div>

        {/* Reminder list */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-4">
            {REMINDER_TYPES.map((reminder, index) => {
              const Icon = reminder.icon;
              const settings = reminders[reminder.id] || { enabled: false, time: reminder.defaultTime };
              
              return (
                <div 
                  key={reminder.id}
                  className="bg-card rounded-2xl p-6 animate-slide-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                  data-testid={`reminder-${reminder.id}`}
                >
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Icon className="w-6 h-6 text-primary" />
                    </div>
                    
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-heading text-lg">{reminder.title}</h3>
                        <Switch
                          checked={settings.enabled}
                          onCheckedChange={() => toggleReminder(reminder.id)}
                          data-testid={`reminder-toggle-${reminder.id}`}
                        />
                      </div>
                      <p className="text-sm text-muted-foreground mb-3">{reminder.description}</p>
                      
                      {settings.enabled && (
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-muted-foreground" />
                            <input
                              type="time"
                              value={settings.time}
                              onChange={(e) => updateTime(reminder.id, e.target.value)}
                              className="bg-secondary/50 rounded-lg px-3 py-1.5 text-sm border-0 focus:ring-1 focus:ring-primary"
                              data-testid={`reminder-time-${reminder.id}`}
                            />
                          </div>
                          <Button 
                            variant="ghost" 
                            size="sm"
                            onClick={() => testNotification(reminder.id)}
                            className="text-xs"
                          >
                            Testa
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Info */}
        <div className="bg-secondary/30 rounded-2xl p-6 text-sm text-muted-foreground flex items-start gap-3">
          <Lightbulb className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
          <p>
            <strong>Tips:</strong> För bästa resultat, installera Livon som app på din hemskärm. 
            Då fungerar påminnelser även när webbläsaren är stängd. På iPhone krävs att appen 
            ligger på hemskärmen (iOS 16.4 eller senare) för att notiser ska fungera.
          </p>
        </div>
      </main>
    </div>
  );
}
